import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { useSidebar } from '../context/SidebarContext';
import {
  Home,
  PlusCircle,
  Search,
  Briefcase,
  Star,
  User,
  Settings,
  LogOut,
  Sun,
  Moon,
  Monitor,
  Lightbulb,
  MoreVertical,
  Sparkles,
  PanelLeft,
} from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';

const Sidebar = () => {
  const { isCollapsed, setIsCollapsed } = useSidebar();
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { theme, setLightMode, setDarkMode, setSystemMode } = useTheme();

  const navigation = [
    { name: 'Dashboard', href: '/dashboard', icon: Home },
    { name: 'Discover Ideas', href: '/discover', icon: Lightbulb },
    { name: 'Create Skill', href: '/builder', icon: Sparkles },
    { name: 'My Skills', href: '/my-skills', icon: Briefcase },
    { name: 'Browse Skills', href: '/browse', icon: Search },
    { name: 'Starred', href: '/starred', icon: Star },
  ];

  const bottomNavigation = [
    { name: 'Profile', href: `/profile/${user?.id}`, icon: User },
  ];

  const isActive = (path) => {
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const getThemeIcon = () => {
    if (theme === 'light') return <Sun className="h-6 w-6" />;
    if (theme === 'dark') return <Moon className="h-6 w-6" />;
    return <Monitor className="h-6 w-6" />;
  };

  return (
    <div
      className={`
        sidebar-transition fixed left-0 top-0 h-screen
        glass-strong border-r flex flex-col
        ${isCollapsed ? 'w-16' : 'w-64'}
        z-50
      `}
    >
      {/* Logo & Collapse Toggle */}
      {!isCollapsed ? (
        <div className="flex items-center justify-between p-4 border-b border-gray-200/50 dark:border-dark-border/50">
          <Link to="/dashboard" className="flex items-center gap-3">
            <img
              src="https://customer-assets.emergentagent.com/job_5cdedf29-6831-4187-8b3b-d6d324530c65/artifacts/vckerg2u_Skillerate%20Icon%20Only.svg"
              alt="Skillerate"
              className="h-8 w-8"
            />
            <span className="font-manrope font-bold text-xl text-text-dark dark:text-dark-text">
              Skillerate
            </span>
          </Link>
          <button
            onClick={() => setIsCollapsed(!isCollapsed)}
            className="h-8 w-8 p-0 rounded-lg hover:bg-gray-100 dark:hover:bg-dark-card transition-colors flex items-center justify-center"
            title="Collapse sidebar"
          >
            <PanelLeft className="h-4 w-4" />
          </button>
        </div>
      ) : (
        <div className="w-full flex flex-col items-center py-4 border-b border-gray-200/50 dark:border-dark-border/50">
          <Link to="/dashboard" className="mb-4">
            <img
              src="https://customer-assets.emergentagent.com/job_5cdedf29-6831-4187-8b3b-d6d324530c65/artifacts/vckerg2u_Skillerate%20Icon%20Only.svg"
              alt="Skillerate"
              className="h-8 w-8"
            />
          </Link>
          <button
            onClick={() => setIsCollapsed(!isCollapsed)}
            className="w-10 h-10 p-0 rounded-lg hover:bg-gray-100 dark:hover:bg-dark-card transition-colors flex items-center justify-center"
            title="Expand sidebar"
          >
            <PanelLeft className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* Main Navigation */}
      <nav className="flex-1 p-3 pb-6 space-y-2 overflow-y-auto overflow-x-hidden">
        {navigation.map((item) => {
          const Icon = item.icon;
          const active = isActive(item.href);
          
          return (
            <Link
              key={item.name}
              to={item.href}
              className={`
                flex items-center gap-3 rounded-xl
                transition-colors duration-200
                ${active
                  ? (isCollapsed 
                      ? 'bg-gray-100 dark:bg-dark-bg-tertiary text-text-dark dark:text-dark-text' 
                      : 'bg-gray-100 dark:bg-dark-bg-tertiary text-text-dark dark:text-dark-text border-l-4 border-gray-300 dark:border-orange-500')
                  : `text-text-dark dark:text-dark-text hover:bg-gray-100/50 dark:hover:bg-dark-bg-hover/50 ${!isCollapsed ? 'border-l-4 border-transparent' : ''}`
                }
                ${isCollapsed ? 'h-10 w-10 justify-center mx-auto' : 'px-3 py-2.5'}
              `}
              title={isCollapsed ? item.name : ''}
            >
              <Icon className="h-5 w-5 flex-shrink-0" />
              {!isCollapsed && (
                <span className={`text-sm ${active ? 'font-medium' : 'font-normal'}`}>{item.name}</span>
              )}
            </Link>
          );
        })}
      </nav>

      {/* Bottom Navigation & User Menu */}
      <div className="border-t border-gray-200/50 dark:border-dark-border/50 p-3 space-y-3">
        {/* Theme Toggle Switch - Light/Dark Only */}
        {!isCollapsed && (
          <div className="px-2">
            <div className="flex items-center gap-0.5 p-0.5 bg-gray-100 dark:bg-dark-bg-tertiary rounded-lg">
              <button
                onClick={setLightMode}
                className={`flex-1 flex items-center justify-center gap-1.5 px-2 py-1.5 rounded-md transition-all text-xs font-medium ${
                  theme === 'light' || theme === 'system'
                    ? 'bg-white dark:bg-dark-bg-hover text-text-dark dark:text-dark-text shadow-sm'
                    : 'text-text-gray dark:text-dark-text-secondary'
                }`}
              >
                <Sun className="h-3.5 w-3.5" />
                <span>Light</span>
              </button>
              <button
                onClick={setDarkMode}
                className={`flex-1 flex items-center justify-center gap-1.5 px-2 py-1.5 rounded-md transition-all text-xs font-medium ${
                  theme === 'dark'
                    ? 'bg-white dark:bg-dark-bg-hover text-text-dark dark:text-dark-text shadow-sm'
                    : 'text-text-gray dark:text-dark-text-secondary'
                }`}
              >
                <Moon className="h-3.5 w-3.5" />
                <span>Dark</span>
              </button>
            </div>
          </div>
        )}
        
        {isCollapsed && (
          <button
            onClick={() => {
              if (theme === 'light' || theme === 'system') setDarkMode();
              else setLightMode();
            }}
            className="h-10 w-10 mx-auto flex items-center justify-center rounded-xl text-text-dark dark:text-dark-text hover:bg-gray-100 dark:hover:bg-dark-card transition-colors"
            title="Toggle theme"
          >
            {getThemeIcon()}
          </button>
        )}

        {/* Profile & Settings */}
        {bottomNavigation.map((item) => {
          const Icon = item.icon;
          const active = isActive(item.href);
          
          return (
            <Link
              key={item.name}
              to={item.href}
              className={`
                flex items-center gap-3 rounded-xl
                transition-colors duration-200
                ${active
                  ? (isCollapsed 
                      ? 'bg-gray-100 dark:bg-dark-bg-tertiary text-text-dark dark:text-dark-text' 
                      : 'bg-gray-100 dark:bg-dark-bg-tertiary text-text-dark dark:text-dark-text border-l-4 border-gray-300 dark:border-orange-500')
                  : `text-text-dark dark:text-dark-text hover:bg-gray-100/50 dark:hover:bg-dark-bg-hover/50 ${!isCollapsed ? 'border-l-4 border-transparent' : ''}`
                }
                ${isCollapsed ? 'h-10 w-10 justify-center mx-auto' : 'px-3 py-2.5'}
              `}
              title={isCollapsed ? item.name : ''}
            >
              <Icon className="h-5 w-5 flex-shrink-0" />
              {!isCollapsed && <span className={`text-sm ${active ? 'font-medium' : 'font-normal'}`}>{item.name}</span>}
            </Link>
          );
        })}

        {/* User Profile */}
        {user && (
          <DropdownMenu>
            <DropdownMenuTrigger
              className={`
                flex items-center gap-3 p-2 rounded-xl w-full
                hover:bg-gray-100 dark:hover:bg-dark-card
                transition-colors duration-200
                ${isCollapsed ? 'justify-center' : ''}
              `}
            >
              <Avatar className="h-8 w-8 flex-shrink-0">
                <AvatarImage src={user.avatar_url} alt={user.username} />
                <AvatarFallback className="bg-gradient-to-br from-primary to-primary-dark text-white">
                  {user.username?.[0]?.toUpperCase()}
                </AvatarFallback>
              </Avatar>
              {!isCollapsed && (
                <>
                  <div className="flex-1 text-left min-w-0">
                    <p className="text-sm font-semibold text-text-dark dark:text-dark-text truncate">
                      {user.display_name || user.username}
                    </p>
                    <p className="text-xs text-text-gray dark:text-dark-text-secondary truncate">
                      @{user.username}
                    </p>
                  </div>
                  <MoreVertical className="h-4 w-4 text-text-gray dark:text-dark-text-secondary flex-shrink-0" />
                </>
              )}
            </DropdownMenuTrigger>
            <DropdownMenuContent side="right" align="end" className="glass-strong w-56">
              <DropdownMenuItem onClick={() => navigate('/profile/edit')} className="cursor-pointer">
                <User className="h-4 w-4 mr-2" />
                Edit Profile
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate('/settings')} className="cursor-pointer">
                <Settings className="h-4 w-4 mr-2" />
                Settings
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleLogout} className="cursor-pointer text-destructive focus:text-destructive">
                <LogOut className="h-4 w-4 mr-2" />
                Sign Out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
    </div>
  );
};

export default Sidebar;

// Add inline CSS to block orange completely
const sidebarStyles = `
  nav a,
  nav a:hover,
  nav a:active,
  nav a:focus,
  nav a[aria-current="page"] {
    background-color: #e5e7eb !important;
    background-image: none !important;
  }
`;
